import { getSupabase } from '@/lib/supabase'
import { buildSeqCodes } from '@/features/expenses/expense-doc-files'
import { uploadCodReductionDoc, deleteCodReductionDoc } from './cod-reduction-storage'
import type {
  CodReductionAttachment,
  CodReductionAttachmentKind,
  CodReductionCreated,
  CodReductionDetail,
  CodReductionListRow,
  CodReductionOrderSnapshot,
  CodReductionPreviewStep,
} from '@/types/db'

const TABLE = 'cod_reduction_requests'

// Bảng có 4 FK tới profiles → embed PHẢI chỉ tên FK (không thì PostgREST báo mơ hồ
// PGRST201). 2 computed field (hàm SQL nhận dòng) phải gọi TÊN TRẦN, không alias.
export const COD_REDUCTION_LIST_SELECT =
  '*, ' +
  'requester:profiles!cod_reduction_requests_created_by_fk(full_name), ' +
  'sale:profiles!cod_reduction_requests_sale_fk(full_name), ' +
  'completer:profiles!cod_reduction_requests_completed_by_fk(full_name), ' +
  'reason:cod_reduction_reasons(name), ' +
  'cod_reduction_is_my_turn, cod_reduction_order_reconciled_at'

const DETAIL_SELECT =
  COD_REDUCTION_LIST_SELECT +
  ', canceller:profiles!cod_reduction_requests_cancelled_by_fk(full_name), ' +
  'steps:cod_reduction_steps(*, actor:profiles!cod_reduction_steps_actor_fk(full_name)), ' +
  'attachments:cod_reduction_attachments(*), ' +
  'history:cod_reduction_history(*, user:profiles!cod_reduction_history_user_fk(full_name))'

export async function getCodReductionDetail(id: number): Promise<CodReductionDetail | null> {
  const { data, error } = await getSupabase().from(TABLE).select(DETAIL_SELECT).eq('id', id).maybeSingle()
  if (error) throw error
  if (!data) return null
  const d = data as unknown as CodReductionDetail
  d.steps = [...(d.steps ?? [])].sort((a, b) => a.step_order - b.step_order)
  d.attachments = [...(d.attachments ?? [])].sort((a, b) => a.id - b.id)
  d.history = [...(d.history ?? [])].sort((a, b) => b.id - a.id)
  return d
}

// Đề nghị của 1 đơn (card ở trang chi tiết đơn) — RLS lọc theo quyền xem đề nghị.
export async function listCodReductionsByOrder(orderId: number): Promise<CodReductionListRow[]> {
  const { data, error } = await getSupabase()
    .from(TABLE)
    .select(COD_REDUCTION_LIST_SELECT)
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return (data ?? []) as unknown as CodReductionListRow[]
}

// --- Đơn để đề nghị (RPC trả jsonb snapshot: đủ điều kiện + COD hiện tại) ------------
export async function findCodReductionOrders(query: string): Promise<CodReductionOrderSnapshot[]> {
  const { data, error } = await getSupabase().rpc('cod_reduction_find_orders', { p_query: query, p_limit: 20 })
  if (error) throw error
  return (data ?? []) as CodReductionOrderSnapshot[]
}
// Ô tìm còn trống: đơn ĐANG GIAO mới nhất người dùng xem được (đơn có đề nghị mở vẫn trả,
// kèm lý do không chọn được). Hàm jsonb → không dính trần 1000 dòng.
export async function listRecentCodReductionOrders(limit = 50): Promise<CodReductionOrderSnapshot[]> {
  const { data, error } = await getSupabase().rpc('cod_reduction_recent_orders', { p_limit: limit })
  if (error) throw error
  return (data ?? []) as CodReductionOrderSnapshot[]
}
export async function getCodReductionOrderInfo(orderId: number): Promise<CodReductionOrderSnapshot> {
  const { data, error } = await getSupabase().rpc('cod_reduction_order_info', { p_order_id: orderId })
  if (error) throw error
  return data as CodReductionOrderSnapshot
}
export async function previewCodReductionChain(orderId: number, reduce: number): Promise<CodReductionPreviewStep[]> {
  const { data, error } = await getSupabase().rpc('cod_reduction_preview_chain', {
    p_order_id: orderId,
    p_reduce: reduce,
  })
  if (error) throw error
  return (data ?? []) as CodReductionPreviewStep[]
}

// --- Hành động (RPC SECURITY DEFINER — DB kiểm mọi luật) ------------------------------
export type CodReductionInput = { orderId: number; newCod: number; reasonId: number; note: string | null }

export async function createCodReduction(input: CodReductionInput): Promise<CodReductionCreated> {
  const { data, error } = await getSupabase().rpc('create_cod_reduction', {
    p_order_id: input.orderId,
    p_new_cod: input.newCod,
    p_reason_id: input.reasonId,
    p_reason_note: input.note,
  })
  if (error) throw error
  return data as CodReductionCreated
}
export async function resubmitCodReduction(id: number, input: Omit<CodReductionInput, 'orderId'>): Promise<void> {
  const { error } = await getSupabase().rpc('resubmit_cod_reduction', {
    p_id: id,
    p_new_cod: input.newCod,
    p_reason_id: input.reasonId,
    p_reason_note: input.note,
  })
  if (error) throw error
}
// stepId = bước người dùng ĐANG THẤY — DB từ chối nếu người khác vừa xử lý (cod_reduction_stale).
export async function approveCodReduction(id: number, stepId: number, comment: string | null): Promise<void> {
  const { error } = await getSupabase().rpc('approve_cod_reduction', { p_id: id, p_step_id: stepId, p_comment: comment })
  if (error) throw error
}
export async function rejectCodReduction(id: number, stepId: number, reason: string): Promise<void> {
  const { error } = await getSupabase().rpc('reject_cod_reduction', { p_id: id, p_step_id: stepId, p_reason: reason })
  if (error) throw error
}
export async function confirmCodReduction(id: number, stepId: number, note: string | null): Promise<void> {
  const { error } = await getSupabase().rpc('confirm_cod_reduction', { p_id: id, p_step_id: stepId, p_note: note })
  if (error) throw error
}
export async function cancelCodReduction(id: number, reason: string | null): Promise<void> {
  const { error } = await getSupabase().rpc('cancel_cod_reduction', { p_id: id, p_reason: reason })
  if (error) throw error
}

// --- Chứng từ -------------------------------------------------------------------------
export type AttachCtx = {
  createdAt: string | null
  employee: string
  startSeq?: number
  onProgress?: (done: number, total: number) => void
  // Gọi sau MỖI file đã lưu (Drive + dòng DB) — lỗi giữa chừng thì caller biết file nào
  // đã xong để khỏi tải lại trùng khi người dùng bấm thử lại.
  onFileSaved?: (index: number) => void
}

// Upload Drive (backend kiểm cod_reduction_can_attach) rồi ghi dòng (policy INSERT kiểm lại).
export async function addCodReductionAttachment(
  requestId: number,
  code: string,
  kind: CodReductionAttachmentKind,
  file: File,
  ctx: AttachCtx,
): Promise<CodReductionAttachment> {
  const up = await uploadCodReductionDoc(file, { requestId, code, kind, createdAt: ctx.createdAt, employee: ctx.employee })
  // uploaded_by để DB tự điền (default auth.uid()) — policy INSERT đòi đúng người gọi.
  const { data, error } = await getSupabase()
    .from('cod_reduction_attachments')
    .insert({
      request_id: requestId,
      code,
      kind,
      file_path: up.path,
      file_name: up.name,
      mime_type: up.mime,
    })
    .select('*')
    .single()
  if (error) throw error
  return data as CodReductionAttachment
}

// NHIỀU file, TUẦN TỰ (giữ đúng thứ tự mã <gốc>-<n>); lỗi ở file nào thì dừng, file trước vẫn giữ.
export async function addCodReductionAttachments(
  requestId: number,
  baseCode: string,
  kind: CodReductionAttachmentKind,
  files: File[],
  ctx: AttachCtx,
): Promise<CodReductionAttachment[]> {
  const codes = buildSeqCodes(baseCode, ctx.startSeq ?? 1, files.length)
  const out: CodReductionAttachment[] = []
  for (let i = 0; i < files.length; i++) {
    out.push(await addCodReductionAttachment(requestId, codes[i]!, kind, files[i]!, ctx))
    ctx.onFileSaved?.(i)
    ctx.onProgress?.(i + 1, files.length)
  }
  return out
}

export async function deleteCodReductionAttachment(filePath: string): Promise<void> {
  await deleteCodReductionDoc(filePath)
}
